// src/pages/admin/LeadsDashboard.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector }      from 'react-redux';
import { fetchLeads, deleteLead, updateStatus }         from '../store/thunks/leadsActions';
import LeadListPanel                      from '../components/leadsComponents/LeadListPanel';
import LeadDetailPanel                    from '../components/leadsComponents/LeadDetailPanel';
import BulkSendModal                      from '../components/leadsComponents/BulkSendModal';
import TemplateModal                      from '../components/leadsComponents/TemplateModal';
import { useNavigate }                    from 'react-router-dom';

const TABS = [
  { key: 'all',             label: 'All' },
  { key: 'new',             label: 'New' },
  { key: 'contacted',             label: 'Intro Sent' },
  { key: 'needs_follow_up', label: 'Follow-Up' },
  { key: 'responded',       label: 'Responded' },
  { key: 'replied',         label: 'PM Replied' },
  { key: 'closed',          label: 'Closed' },
];

export default function LeadsDashboard() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { leads = [], loading } = useSelector(s => s.leads);
  const { adminInfo }           = useSelector(s => s.login);

  const [selectedLeadId,    setSelectedLeadId]    = useState(null);
  const [activeTab,         setActiveTab]         = useState('all');
  const [showBulkSend,      setShowBulkSend]      = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null
const lastFetchRef = React.useRef(0);
const doFetchLeads = React.useCallback(() => {
    return dispatch(fetchLeads()).then(() => {
      lastFetchRef.current = Date.now();
      console.log(lastFetchRef)
    });
  }, [dispatch]);

  // initial fetch
  useEffect(() => {
    doFetchLeads();
  }, [doFetchLeads]);

  // poll every minute if >3m stale
  useEffect(() => {
    const iv = setInterval(() => {
      if (Date.now() - lastFetchRef.current > 3 * 60 * 1000) {
        doFetchLeads();
      }
    }, 60 * 1000);
    return () => clearInterval(iv);
  }, [doFetchLeads]);


  useEffect(() => {
    if (selectedLeadId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    // cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedLeadId]);


    useEffect(() => {
    if (loading) return; 
    const now = Date.now();
    leads.forEach(lead => {
      if (
        lead.status !== 'needs_follow_up' &&
        lead.status !== 'closed' &&
        Array.isArray(lead.messages)
      ) {
        // find the most recent outbound
        const lastOutbound = [...lead.messages]
          .reverse()
          .find(m => m.direction === 'outbound');
        if (lastOutbound) {
          const sentAt = new Date(lastOutbound.timestamp).getTime();
          if (now - sentAt > 3 * 24 * 60 * 60 * 1000) {
            dispatch(updateStatus({ leadId: lead._id, status: 'needs_follow_up' }));
          }
        }
      }
    });
  }, [leads, loading, dispatch]);

  const handleClosePanel = () => {
    setSelectedLeadId(null);
    // doFetchLeads();
  };

  const handleDeleteLead = id => {
    if (window.confirm('Delete this lead?')) {
      dispatch(deleteLead(id));
      setSelectedLeadId(null);
    }
  };

  // count by status
const tabCounts = TABS.reduce((acc, t) => {
  acc[t.key] = leads.filter(l =>
    !l.deleted &&
    l.status !== 'emailed' &&            // exclude emailed
    (t.key === 'all' ? true : l.status === t.key)
  ).length;
  return acc;
}, {});
  // filtered list
const visibleLeads = leads.filter(l =>
  !l.deleted &&
  l.status !== 'emailed' &&              // exclude emailed
  (activeTab === 'all' ? true : l.status === activeTab)
);
// after computing visibleLeads:
const sortedLeads = React.useMemo(() => {
  if (!sortOrder) return visibleLeads;
  return [...visibleLeads].sort((a, b) => {
    const aCount = a.enrichment?.property_count_estimate || 0;
    const bCount = b.enrichment?.property_count_estimate || 0;
    return sortOrder === 'asc' ? aCount - bCount : bCount - aCount;
  });
}, [visibleLeads, sortOrder]);

  return (
    <div className="flex h-auto w-full bg-[#F9FAFB] overflow-auto">
      {/* ——— LEFT PANEL (list) ——— */}
      <div className="w-[1050px] flex-shrink-0 border-r border-[#E5E7EB] p-5 flex flex-col">
        {/* Totals & Gmail connect */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-5 rounded-[16px] shadow-[0_1px_4px_rgba(0,0,0,0.05)]">
            <p className="text-sm text-[#6B7280]">Total Leads</p>
            <p className="text-2xl font-semibold text-[#1F2937]">{tabCounts.all}</p>
          </div>
          {!adminInfo.automationAllowed && (
            <button
              className="col-span-2 px-4 py-2 rounded-[12px] bg-[#DB4437] text-white"
              onClick={() => navigate('/settings')}
            >
              Connect Gmail
            </button>
          )}
        </div>
<div className="flex items-center justify-end mb-2 space-x-2">
  <span className="text-sm text-gray-600">Sort by properties:</span>
  <button
    className={`px-2 py-1 text-sm rounded ${sortOrder==='asc' ? 'bg-[#2563EB] text-white' : 'bg-white text-[#374151] hover:bg-[#F3F4F6]'}`}
    onClick={() => setSortOrder(order => order==='asc' ? null : 'asc')}
  >
    Asc ↑
  </button>
  <button
    className={`px-2 py-1 text-sm rounded ${sortOrder==='desc' ? 'bg-[#2563EB] text-white' : 'bg-white text-[#374151] hover:bg-[#F3F4F6]'}`}
    onClick={() => setSortOrder(order => order==='desc' ? null : 'desc')}
  >
    Desc ↓
  </button>
    <button
    className="px-2 py-1 text-sm rounded bg-white text-[#374151] hover:bg-[#F3F4F6]"
    onClick={() => setSortOrder(null)}
  >
    Clear
  </button>
</div>
        {/* Tabs */}
        <div className="flex space-x-2 mb-4">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`
                flex-1 text-center px-3 py-2 rounded-[12px] text-sm font-medium
                ${activeTab === t.key
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-white text-[#374151] hover:bg-[#F3F4F6]'}
              `}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label} ({tabCounts[t.key]})
            </button>
          ))}
        </div>

        {/* Bulk / Templates */}
        <div className="flex gap-2 mb-4">
          <button
            className="px-4 py-2 rounded-[12px] bg-[#2563EB] text-white"
            onClick={() => setShowBulkSend(true)}
          >
            Bulk Send
          </button>
          <button
            className="px-4 py-2 rounded-[12px] bg-[#10B981] text-white"
            onClick={() => setShowTemplateModal(true)}
          >
            Edit Templates
          </button>
        </div>

        {/* Lead list */}
        <div className="flex-1 overflow-y-auto">
<LeadListPanel
  leads={sortedLeads}
  loading={loading}
  onSelectLead={setSelectedLeadId}
/>

        </div>
      </div>

      {/* ——— DETAIL PANEL OVERLAY ——— */}
      {selectedLeadId && (
        <>
          {/* full-screen backdrop to catch outside clicks */}
          <div
            className="fixed inset-0 bg-transparent z-20"
            onClick={handleClosePanel}
          />

          {/* fixed 450px detail panel */}
                   <div
            className="fixed top-0 right-0 h-full w-[970px] bg-white z-30 shadow-xl
                       flex flex-col overflow-y-auto"
          >

<LeadDetailPanel
  leadId={selectedLeadId}
  onDelete={handleDeleteLead}
  onClose={() => setSelectedLeadId(null)}
  onEmailSent={newStatus => {
    // 1) close the panel
handleClosePanel();
    // 3) switch to the tab matching the new status:
    setActiveTab(newStatus);
  }}
/>

          </div>
        </>
      )}

      {/* MODALS */}
      {showBulkSend && (
        <BulkSendModal leads={visibleLeads} onClose={() => setShowBulkSend(false)} />
      )}
      {showTemplateModal && (
        <TemplateModal onClose={() => setShowTemplateModal(false)} />
      )}
    </div>
  );
}
